/**
 * Optional honorific / salutation titles for customer names.
 *
 * Mirror of the backend list in
 * apps/api/src/common/constants/customer-titles.ts — keep the two in sync.
 *
 * Used as a dropdown convenience; "Lain-lain" lets staff type a free-text title,
 * so the stored value is just a bounded string. Titles are stored verbatim and
 * never title-cased (e.g. "Dato'" keeps its apostrophe and casing).
 */
export const CUSTOMER_TITLES = [
  'Encik',
  'Cik',
  'Puan',
  'Tuan',
  'Mr',
  'Ms',
  'Mrs',
  'Dr',
  "Dato'",
  'Datin',
  'Datuk',
  'Tan Sri',
  'Tun',
  'Toh Puan',
  'Tunku',
  'Nik',
] as const;

/**
 * Honorific prefixes recognised when UPPER-CASING an already-JOINED display name
 * (a pre-joined `customerName` snapshot, where title and name are no longer
 * separate). Superset of the dropdown CUSTOMER_TITLES plus common FREE-TEXT
 * honorifics ("Lain-lain") so "Prof Ahmad" keeps the title as entered rather than
 * "PROF AHMAD". Display-only. Mirror of the backend list in
 * apps/api/src/common/constants/customer-titles.ts — keep in sync.
 */
export const DISPLAY_TITLE_PREFIXES: readonly string[] = [
  ...CUSTOMER_TITLES,
  'Prof',
  'Professor',
  'Sir',
  'Haji',
  'Hajah',
  'Hj',
  'Hjh',
  'Tuan Haji',
  'Puan Hajah',
  'Ir',
  'Ar',
  'Ustaz',
  'Ustazah',
  'Sheikh',
  'Syed',
  'Sharifah',
];

/** Sorted longest-first so "Tuan Haji Ahmad" matches "Tuan Haji", not "Tuan". */
const DISPLAY_TITLE_PREFIXES_BY_LENGTH = [...DISPLAY_TITLE_PREFIXES].sort(
  (a, b) => b.length - a.length,
);

/** Max length accepted for a (possibly free-text) customer title. */
export const CUSTOMER_TITLE_MAX_LENGTH = 30;

/**
 * Join an optional title with a name for display. The NAME is upper-cased for
 * display (the title/honorific is shown as entered — "Dato'" reads wrong as
 * "DATO'"). Returns the name alone when no title is set (no leading space, no
 * "undefined").
 *
 * formatCustomerName("Dato'", "Ahmad")  -> "Dato' AHMAD"
 * formatCustomerName(null, "Ahmad")      -> "AHMAD"
 * formatCustomerName("Encik", "")        -> "Encik"
 */
export function formatCustomerName(
  title: string | null | undefined,
  name: string | null | undefined,
): string {
  const t = (title ?? '').trim();
  const n = (name ?? '').trim().toUpperCase();
  if (t && n) return `${t} ${n}`;
  return t || n;
}

/**
 * Upper-case a customer name for display in the many list/table/report cells
 * that render a pre-joined `customerName` string directly (no separate
 * title/name fields). A leading known honorific is preserved as entered — only
 * the NAME part is upper-cased — so "Dato' Ahmad" renders "Dato' AHMAD", not
 * "DATO' AHMAD" (consistent with formatCustomerName/joinTitleNameUpper).
 * Returns the given fallback (default '—') for empty/nullish input.
 */
export function displayName(name: string | null | undefined, fallback = '—'): string {
  const n = (name ?? '').trim();
  if (!n) return fallback;
  // Strip a leading recognised honorific (dropdown OR common free-text), upper-
  // case the remainder, re-prepend the title in its canonical casing. Longest
  // prefix wins so "Tuan Haji Ahmad" keeps "Tuan Haji", not just "Tuan".
  for (const title of DISPLAY_TITLE_PREFIXES_BY_LENGTH) {
    if (n.toLowerCase().startsWith(title.toLowerCase() + ' ')) {
      return `${title} ${n.slice(title.length + 1).toUpperCase()}`;
    }
  }
  return n.toUpperCase();
}
